// The module dropdown at the top left (spec, The header: 1). A plain label with one
// module; a WAI-ARIA menu button with two or more. Choosing navigates to the module's
// home; there is no reload, because every module is in this one app.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { nextMenuIndex } from './menu-keys.js';

export default function ModuleSwitcher({ active, available }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const wrapRef = useRef(null);
  const label = active?.label ?? 'Adora Ops';

  useEffect(() => {
    if (!open) return undefined;
    itemRefs.current[focusIndex]?.focus();
    function onDown(event) {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, focusIndex]);

  if (available.length < 2) {
    return <span className="font-semibold tracking-tight text-ink">{label}</span>;
  }

  function openAt(index) { setFocusIndex(index); setOpen(true); }
  function close() { setOpen(false); triggerRef.current?.focus(); }
  function choose(mf) { setOpen(false); navigate(mf.home); }

  function onTriggerKey(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAt(Math.max(0, available.findIndex((mf) => mf.id === active?.id)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(available.length - 1);
    }
  }

  function onMenuKey(event) {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key === 'Tab') { setOpen(false); return; }
    const next = nextMenuIndex(event.key, focusIndex, available.length);
    if (next !== null) { event.preventDefault(); setFocusIndex(next); }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="platform-module-menu"
        onClick={() => (open ? setOpen(false) : openAt(Math.max(0, available.findIndex((mf) => mf.id === active?.id))))}
        onKeyDown={onTriggerKey}
        className="flex items-center gap-1 rounded-md px-1.5 py-1 font-semibold tracking-tight text-ink
                   hover:bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
      >
        {label}
        <svg viewBox="0 0 20 20" className="size-4 text-ink-muted" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <ul
          id="platform-module-menu"
          role="menu"
          aria-label="Switch module"
          onKeyDown={onMenuKey}
          className="absolute left-0 z-50 mt-1 min-w-48 rounded-md bg-surface py-1 shadow-lg ring-1 ring-line"
        >
          {available.map((mf, i) => {
            const current = mf.id === active?.id;
            return (
              <li key={mf.id} role="none">
                <button
                  ref={(el) => { itemRefs.current[i] = el; }}
                  type="button"
                  role="menuitem"
                  tabIndex={i === focusIndex ? 0 : -1}
                  aria-current={current ? 'page' : undefined}
                  onClick={() => choose(mf)}
                  className="flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left text-sm text-ink
                             hover:bg-sunken focus:bg-sunken focus:outline-none"
                >
                  {mf.label}
                  {current && <span aria-hidden="true" className="text-brand-600 dark:text-brand-400">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
